import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@jupi/ui'
import type {
  CreateAvailabilityInput,
  InstructorAvailability,
  MyAircraft,
  SuggestLessonSlotsInput,
  SuggestLessonSlotsResult,
} from '@jupi/shared'
import type { Booking } from './queries'

// Instructor lessons, availability and the booking wizard's suggestions.
// Keyed under 'bookings' where a booking changes them, so the booking
// mutations refresh them too.

export interface LessonStudent {
  userId: string
  displayName: string
}

export function useMyLessons(params: { from: Date; to: Date; studentUserId?: string | null; enabled?: boolean }) {
  const query = new URLSearchParams({ from: params.from.toISOString(), to: params.to.toISOString() })
  if (params.studentUserId) query.set('studentUserId', params.studentUserId)
  return useQuery({
    queryKey: ['bookings', 'lessons', query.toString()],
    queryFn: () =>
      api.get<{ lessons: Booking[]; students: LessonStudent[] }>(`/api/school/instructors/me/lessons?${query}`),
    enabled: params.enabled ?? true,
    placeholderData: (previous) => previous,
  })
}

export function useAvailability(userId: string | undefined, range: { from: Date; to: Date }) {
  const query = `from=${range.from.toISOString()}&to=${range.to.toISOString()}`
  return useQuery({
    queryKey: ['bookings', 'availability', userId, query],
    queryFn: () => api.get<InstructorAvailability>(`/api/school/instructors/${userId}/availability?${query}`),
    enabled: Boolean(userId),
    placeholderData: (previous) => previous,
  })
}

export function useAvailabilityMutations(userId: string | undefined) {
  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['bookings', 'availability'] })
  const base = `/api/school/instructors/${userId}/availability`
  return {
    // One request, one transaction: all of them are added or none.
    add: useMutation({
      mutationFn: (items: CreateAvailabilityInput[]) => api.post(`${base}/batch`, { items }),
      onSuccess: refresh,
    }),
    setOpen: useMutation({
      mutationFn: (openBooking: boolean) => api.put(`${base}/mode`, { openBooking }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (target: { kind: 'window' | 'rule'; id: string }) =>
        api.delete(`${base}/${target.kind === 'window' ? 'windows' : 'rules'}/${target.id}`),
      onSuccess: refresh,
    }),
  }
}

export function useSuggestSlots() {
  return useMutation({
    mutationFn: (input: Partial<SuggestLessonSlotsInput> & Pick<SuggestLessonSlotsInput, 'enrolmentId'>) =>
      api.post<SuggestLessonSlotsResult>('/api/bookings/suggest', input),
  })
}

// The aircraft a member can fly and their favourite.
export function useMyAircraft() {
  return useQuery({ queryKey: ['my-aircraft'], queryFn: () => api.get<MyAircraft>('/api/my-aircraft') })
}

export function useSaveMyAircraft() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: MyAircraft) => api.put<MyAircraft>('/api/my-aircraft', input),
    onSuccess: (saved) => queryClient.setQueryData(['my-aircraft'], saved),
  })
}
